/**
 * What the visitor tracker saw, laid out the way its own screen lays it out.
 *
 * It opens inside the pipeline row rather than in a sheet over it, because
 * these two lists are read side by side — somebody looks a name up in the
 * tracker and then looks for it here — and the second one should not make
 * them re-learn where anything is.
 *
 * Three blocks, in the tracker's order:
 *
 *   Captured details   what they typed, verbatim
 *   Captures           one card per visit: when, from where, on what, and
 *                      the attribution behind it
 *   Page journey       the pages, in order, with how long on each
 *
 * Everything here is observation. What we are *doing* about it — the stage,
 * the owner, the activity trail — is the deal sheet, behind the eye button.
 * Keeping the two apart is the point: this panel never invents a value, so
 * a blank means the tracker did not know, not that nobody has filled it in.
 */
import { useApp } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { spent } from '../lib/format.js';
import { parseDate } from '../lib/dates.js';

/** "company_name" → "Company name". The tracker's keys are form field names. */
const label = k => k
  .replace(/\[\]$/, '')
  .replace(/[_-]+/g, ' ')
  .replace(/^./, c => c.toUpperCase());

/** "/payment/signup" — the path, without the tracking that follows it. */
const pathOf = (url) => {
  const p = String(url || '').replace(/^https?:\/\/[^/]+/, '').split(/[?#]/)[0];
  return p || '/';
};

/** "thexyra.ai" — set quiet, because it is the same on every row. */
const hostOf = url => (String(url || '').match(/^https?:\/\/([^/]+)/) || [])[1] || '';

/**
 * How many parameters are riding on the URL.
 *
 * Counted rather than printed. A remarketing link carries a gclid of three
 * hundred characters, and showing it buries "/book-demo" — which is the one
 * part of the address anybody reads. The tags themselves are listed properly
 * under Came from.
 */
const queryCount = (url) => {
  const q = String(url || '').split('?')[1];
  return q ? q.split('&').filter(Boolean).length : 0;
};

/**
 * The UTM tags as the tracker prints them: short keys, monospaced, in a row.
 *
 * `src` rather than `utm_source` because five of these across a card is a
 * paragraph of boilerplate with four useful words in it.
 */
function AttributionTags({ utm, ad }) {
  const tags = [
    utm.source && ['src', utm.source],
    utm.medium && ['med', utm.medium],
    utm.campaign && ['camp', utm.campaign],
    utm.term && ['term', utm.term],
    utm.content && ['ad', utm.content]
  ].filter(Boolean);
  if (!tags.length && !ad) return null;

  return (
    <div className="lcap-tags">
      {tags.map(([k, v]) => (
        <span key={k} className={`ltag ${k}`}><b>{k}:</b>{v}</span>
      ))}
      {/* The click id itself is three hundred characters of opaque token and
          says nothing to a person; which network it came from does. */}
      {ad && ad.type ? <span className="ltag id"><Icon name="zap" />{ad.type}</span> : null}
    </div>
  );
}

/**
 * "4 pages · 11 clicks · 3 scrolls" — what the trail is made of, in the
 * order somebody asks for it. Kinds with nothing in them are left out, so
 * a plain pageview visit reads as one phrase rather than three zeroes.
 */
const ORDER = [
  ['pageview', 'page', 'pages'],
  ['click', 'click', 'clicks'],
  ['scroll', 'scroll', 'scrolls'],
  ['exit', 'exit', 'exits']
];
const eventSummary = n => ORDER
  .filter(([k]) => n[k])
  .map(([k, one, many]) => `${n[k]} ${n[k] === 1 ? one : many}`)
  .join(' · ');

/*
 * How many of each, from whichever shape arrived.
 *
 * A list response carries `eventStats` — the counts alone, because thirty
 * thousand click records is 70% of everything the board downloads and it
 * draws none of them. A single deal read carries the events themselves. This
 * line reads the same either way; the window that wants the trail asks for
 * the deal.
 */
const statsOf = visit => {
  if (visit.eventStats) return visit.eventStats;
  const events = visit.events || [];
  const n = { total: events.length };
  events.forEach(e => { n[e.type] = (n[e.type] || 0) + 1; });
  return n;
};

/** One visit. */
function Capture({ visit, fmtDate, n, of, who, dealId, index }) {
  const { setSheet } = useApp();
  const at = parseDate(visit.at);
  const where = [visit.city, visit.country].filter(Boolean).join(', ');
  const on = [visit.device, visit.browser].filter(Boolean).join(' · ');
  const journey = visit.journey || [];
  const stats = statsOf(visit);

  return (
    <li className="lcapture">
      <div className="lcap-head">
        {/* Newest first, so visit 1 is the most recent - numbered from the
            other end would have the first thing anybody reads labelled 4. */}
        <span className="lcap-n" aria-hidden="true">{of - n}</span>
        <b>{at ? fmtDate(at, true) : 'unknown date'}</b>
        {(visit.types || []).map((t, i) => (
          <span
            key={`${t}-${i}`}
            className={`lcap ${(visit.states || [])[i] === 'submitted' ? 'sub' : 'part'}`}
          >
            <i /><b>{t}</b>
            <em>{(visit.states || [])[i] === 'submitted' ? 'Submitted' : 'Partially Submitted'}</em>
          </span>
        ))}
        {visit.source ? <span className="lcap-src">{visit.source}</span> : null}
      </div>

      {/*
        * The page they landed on, as a page.
        *
        * It was the raw URL, and a remarketing link is two hundred characters
        * of gclid with "/book-demo" buried at the front — so the one thing
        * worth reading was the one thing you could not see. Host quiet, path
        * loud, and a count of the tracking parameters rather than the
        * parameters themselves: they are listed properly under Came from.
        */}
      {visit.landing ? (
        <a className="lcap-url" href={visit.landing} target="_blank" rel="noreferrer" title={visit.landing}>
          <Icon name="link" />
          <span className="lu-host">{hostOf(visit.landing)}</span>
          <span className="lu-path">{pathOf(visit.landing)}</span>
          {queryCount(visit.landing)
            ? <span className="lu-q">+{queryCount(visit.landing)} tracking</span>
            : null}
        </a>
      ) : null}

      <div className="lcap-facts">
        {where ? <span><Icon name="pin" />{where}</span> : null}
        {on ? <span><Icon name="grid" />{on}</span> : null}
        {visit.pages ? <span><Icon name="file" />{visit.pages} page{visit.pages === 1 ? '' : 's'}</span> : null}
        {visit.seconds ? <span><Icon name="clock" />{spent(visit.seconds)}</span> : null}
      </div>

      {/* Where they came from, and the tags that prove it. */}
      {visit.source || visit.referrer || (visit.utm && visit.utm.source) || visit.ad ? (
        <div className="lcap-from">
          <div className="lcap-fromhead">
            <span className="llabel">Came from</span>
            {visit.source ? <span className="lcap-srcpill">{visit.source}</span> : null}
          </div>

          {visit.social && visit.social.platform ? (
            <div className="lcap-social">
              <Icon name="flow" />
              <b>{visit.social.platform}</b>
              {visit.social.label ? <span>· {visit.social.label}</span> : null}
            </div>
          ) : null}

          {visit.referrer
            ? (
              <a className="lcap-ref" href={visit.referrer} target="_blank" rel="noreferrer">
                <Icon name="link" />{visit.referrer}
              </a>
            )
            : <p className="lcap-noref">No referrer (direct visit, bookmark, or referrer was stripped)</p>}

          <AttributionTags utm={visit.utm || {}} ad={visit.ad} />
        </div>
      ) : null}

      {/*
        * Everything they did, in one line.
        *
        * The tracker added `events` to the API after this was built against
        * `journey`, which is pageviews alone. This is the whole trail — every
        * click with the element behind it, every scroll with its depth — and
        * it is the difference between "they were on /book-demo for 86
        * seconds" and "they picked XYRA Books, chose United Arab Emirates,
        * hit a validation error on the phone field and opened the chat
        * instead of submitting".
        *
        * It was printed here in full, which at two hundred events made the
        * row taller than the window and pushed the rest of the board off
        * screen. The count and what it is made of is what anybody reads on
        * the way past; the trail itself opens in a window. Rows synced
        * before events existed fall back to the pageview list rather than
        * going blank.
        */}
      {stats.total ? (
        <button
          type="button"
          className="lcap-jbtn"
          onClick={() => setSheet({
            kind: 'pageJourney',
            visit,
            /* Where to go for the trail itself, which the list response does
               not carry. */
            dealId,
            index,
            who,
            at: at ? fmtDate(at, true) : ''
          })}
        >
          <Icon name="flow" />
          <b>{`${stats.total} event${stats.total === 1 ? '' : 's'}`}</b>
          <span>{eventSummary(stats)}</span>
          <Icon name="chev" />
        </button>
      ) : journey.length ? (
        <div className="lcap-journey">
          <span className="llabel">Page journey</span>
          <ol>
            {journey.map((j, n) => (
              <li key={`${j.url}-${n}`} title={j.url}>
                <span className="ljstep">{n + 1}</span>
                <span className="ljtitle">{j.title || pathOf(j.url)}</span>
                <span className="ljpath">{pathOf(j.url)}</span>
                {j.seconds ? <em>{spent(j.seconds)}</em> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </li>
  );
}

export default function LeadCapture({ f, name, fmtDate }) {
  const visits = f.sessions || [];
  const first = parseDate(f.requested);

  /*
   * Who they are, then what they typed.
   *
   * The tracker puts the name, the address and the organisation at the top of
   * its own Captured details block, and this showed only the form fields — so
   * the three things somebody reads first were the three that were missing.
   *
   * They come off the lead rather than out of `fields`: pickFields on the
   * server allowlists marketing attributes and drops `name` and `email`
   * deliberately, because the lead already carries both and storing them
   * twice is how two copies drift apart. Marked `reach` so they colour as
   * contact details, and never overwritten by a form field of the same name.
   */
  const identity = {
    ...(name ? { Name: name } : {}),
    ...(f.email ? { Email: f.email } : {}),
    ...(f.phone ? { Phone: f.phone } : {}),
    /* Labelled Network, not Company. The tracker resolves it from the IP
       address, so it is the internet provider far more often than the
       employer - "GTO Oman Adsl" is not where anybody works. */
    ...(f.network ? { Network: f.network } : {})
  };
  const fields = { ...identity, ...(f.fields || {}) };

  return (
    <div className="lopen">
      {Object.keys(fields).length ? (
        <section>
          <h5 className="lhead"><Icon name="file" />Captured details</h5>
          <div className="lfields">
            {Object.entries(fields).map(([k, v]) => (
              <div key={k}>
                <dt>{label(k)}</dt>
                <dd>{String(v)}</dd>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {visits.length ? (
        <section>
          <h5 className="lhead">
            <Icon name="zap" />
            {`${f.visits || visits.length} capture${(f.visits || visits.length) === 1 ? '' : 's'}`}
            {first ? <small>{`first seen ${fmtDate(first)}`}</small> : null}
          </h5>
          <ol className="lcaptures">
            {visits.map((v, i) => (
              <Capture
                key={`${v.at}-${i}`}
                visit={v}
                fmtDate={fmtDate}
                n={i}
                of={visits.length}
                index={i}
                dealId={f.dealId}
                who={name || f.email || f.phone}
              />
            ))}
          </ol>
          {/* The tracker keeps about thirty days of sessions and this panel
              carries the last six, so a long-running visitor is summarised
              rather than silently cut. */}
          {f.visits > visits.length ? (
            <p className="lmorev">{`Showing the last ${visits.length} of ${f.visits} visits.`}</p>
          ) : null}
        </section>
      ) : null}

      {/* Said once, at the bottom, rather than as an empty "IP address" row
          that looks like a value nobody filled in.
          The click trail and scroll depth used to be on this line too. The
          tracker added them to the API on 24 Sept 2026 and they are in the
          list above now; the address is the only thing still held back, and
          that one is deliberate. */}
      <p className="lnotsent">
        The tracker also records the visitor&rsquo;s IP address. Its API does not
        send it.
      </p>
    </div>
  );
}
